import { getAccessToken } from './getAccessToken';
import moment from 'moment';
import { normalizeGuid } from '../utils/guid';
import { ENVIRONMENT_URL, DATAVERSE_TABLES } from '../constants/dataverse';

const environmentUrl = ENVIRONMENT_URL;

export interface BakingSievesAndMagnetsRecordData {
  cr3ea_criteria: string;
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string | null;
  cr3ea_defectremarks: string | null;
  cr3ea_platform: string;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string | null;
  cr3ea_description: string;
  cr3ea_productname: string;
  cr3ea_executivename: string;
}

export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

function getShiftValue(): string {
  if (typeof sessionStorage === 'undefined') return 'shift 1';
  return sessionStorage.getItem('shiftValue') || 'shift 1';
}

export function collectBakingSievesAndMagnetsRecord(
  cycleNum: number,
  criteria: string,
  title: string,
  timeValue: string,
  executiveName: string,
  defectRemarks: string | null,
  QualityTourId: string,
  UserName: string | null
): BakingSievesAndMagnetsRecordData {
  return {
    cr3ea_criteria: criteria || 'N/A',
    cr3ea_qualitytourid: normalizeGuid(QualityTourId),
    cr3ea_title: `QA_${moment().format('MM-DD-YYYY')}`,
    cr3ea_cycle: `Cycle-${cycleNum}`,
    cr3ea_shift: getShiftValue(),
    cr3ea_defectremarks: defectRemarks || null,
    cr3ea_platform: 'PWA',
    cr3ea_tourstartdate: moment().format('MM-DD-YYYY'),
    cr3ea_observedby: UserName || null,
    cr3ea_description: title || 'N/A',
    cr3ea_productname: timeValue || 'N/A',
    cr3ea_executivename: executiveName || 'N/A',
  };
}

export async function savesectionApicall(
  data: BakingSievesAndMagnetsRecordData[],
  optionalAccessToken?: string
): Promise<SaveResponse> {
  try {
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = true);

    let accessToken: string | undefined = optionalAccessToken;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = DATAVERSE_TABLES.SIEVES_AND_MAGNETS;
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results: any[] = [];
    for (const record of data) {
      let response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(record),
      });

      if (response.status === 401) {
        localStorage.removeItem("access_token");
        const retryToken = await getAccessToken(undefined, undefined, true);
        const retryAccessToken = (retryToken?.token || "").toString().trim();
        if (retryAccessToken) {
          const retryHeaders = {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "OData-MaxVersion": "4.0",
            "OData-Version": "4.0",
            "Prefer": "return=representation",
            "Authorization": `Bearer ${retryAccessToken}`,
          };
          response = await fetch(apiUrl, {
            method: "POST",
            headers: retryHeaders,
            body: JSON.stringify(record),
          });
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      const result = await response.json();
      results.push(result);
    }

    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results,
    };
  } catch (error) {
    console.error("Error saving Baking Sieves And Magnets records:", error);
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to save records. Please try again.",
      data: [],
    };
  }
}

export interface BakingSievesAndMagnetsCycleData {
  cycleNum: string;
  timeValue: string;
  executiveName: string;
  observedtime: string;
  items: { criteria: string; title: string; status: 'okay' | 'not-okay' | null; remarks: string }[];
}

export async function fetchCycleData(
  QualityTourId: string,
  optionalAccessToken?: string | null
): Promise<BakingSievesAndMagnetsCycleData[]> {
  try {
    let accessToken: string | undefined = optionalAccessToken ?? undefined;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = DATAVERSE_TABLES.SIEVES_AND_MAGNETS;
    const qualityTourGuid = normalizeGuid(QualityTourId);

    const selectFields = [
      "cr3ea_cycle",
      "cr3ea_criteria",
      "cr3ea_description",
      "cr3ea_productname",
      "cr3ea_executivename",
      "cr3ea_defectremarks",
      "createdon",
    ].join(",");

    const buildUrl = (filter: string) =>
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}` +
      `?$filter=${encodeURIComponent(filter)}` +
      `&$select=${selectFields}&$orderby=createdon asc`;

    let filter = `cr3ea_qualitytourid eq '${qualityTourGuid}'`;
    let response = await fetch(buildUrl(filter), { headers });

    if (response.status === 401) {
      localStorage.removeItem("access_token");
      const retryToken = await getAccessToken(undefined, undefined, true);
      const retryAccessToken = (retryToken?.token || "").toString().trim();
      if (retryAccessToken) {
        const retryHeaders = {
          "Accept": "application/json",
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
          "Authorization": `Bearer ${retryAccessToken}`,
        };
        response = await fetch(buildUrl(filter), { headers: retryHeaders });
      }
    }

    if (!response.ok) {
      const errText = await response.text();
      if (errText.includes("Unrecognized") || errText.includes("Edm.") || errText.includes("$filter")) {
        filter = `_cr3ea_qualitytourid_value eq '${qualityTourGuid}'`;
        response = await fetch(buildUrl(filter), { headers });
        if (!response.ok) {
          throw new Error(`Failed to fetch data (retry): ${response.status} - ${await response.text()}`);
        }
      } else {
        throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
      }
    }

    const data = await response.json();
    const byKey: Record<string, BakingSievesAndMagnetsCycleData> = {};

    (data.value || []).forEach((record: any) => {
      const cycleNum = (record.cr3ea_cycle || "").replace("Cycle-", "").trim();
      if (!cycleNum) return;

      const key = `${cycleNum}|${record?.cr3ea_productname ?? ''}|${record?.cr3ea_executivename ?? ''}`;
      if (!byKey[key]) {
        byKey[key] = {
          cycleNum,
          timeValue: record?.cr3ea_productname ?? "",
          executiveName: record?.cr3ea_executivename ?? "",
          observedtime: record?.createdon ?? "",
          items: [],
        };
      }

      const hasDefect = record?.cr3ea_defectremarks && record.cr3ea_defectremarks.trim() !== '';
      byKey[key].items.push({
        criteria: record?.cr3ea_criteria ?? "",
        title: record?.cr3ea_description ?? "",
        status: hasDefect ? 'not-okay' : 'okay',
        remarks: record?.cr3ea_defectremarks ?? "",
      });
    });

    return Object.values(byKey);
  } catch (error) {
    console.error("Error fetching Baking Sieves And Magnets cycle data:", error);
    return [];
  }
}
